/**
 * Past conversations.
 *
 * The other half of the panel's view flip. History is scoped to the signed-in user and
 * cleared when a different one signs in — see `claimFor` in the store, and note that
 * conversations can quote company data, so inheriting them across accounts on a shared
 * machine would be a leak rather than a convenience.
 *
 * VRG-227 — search + multi-select + bulk delete. History is kept until the user deletes
 * it (no auto-expiry beyond `MAX_HISTORY` falling the oldest off), which is what makes
 * the delete affordances here load-bearing rather than incidental: a user with months of
 * history needs a way to find one conversation and a way to clear a batch of them, not
 * just a one-at-a-time X.
 *
 * Two scoping decisions, stated here so they don't have to be reverse-engineered later:
 *
 *   - SELECTION SURVIVES FILTERING. Checking a row and then typing a search that hides
 *     it does NOT drop it from the pending selection — "selected" means what the user
 *     explicitly checked, not "currently checked AND currently visible". "Delete
 *     selected" acts on the true selected set and the confirm modal's count reflects it,
 *     including anything hidden by the current search.
 *   - "SELECT ALL" AND "DELETE ALL" ARE SCOPED TO THE VISIBLE (FILTERED) LIST, not the
 *     whole history. A single click on either should never reach past what's on screen —
 *     that's the Gmail-style "select all in this view" convention, not "select every
 *     conversation you've ever had, including the ones this search is hiding". The label
 *     stays a plain "Delete all" either way: the confirm modal names the real count
 *     ("Delete 3 conversations?"), so the truth is carried at the moment of commitment
 *     rather than by a label that has to change shape to stay honest.
 *   - ONLY ONE DELETE ACTION IS OFFERED AT A TIME. "Delete selected" shows once a
 *     selection exists; "Delete all" shows only while there is none. Side by side they
 *     were two labels a glance apart, differing by one word, with enormously different
 *     blast radius.
 */
import React, { useMemo, useState } from 'react'

import { HistoryIcon, SearchXIcon, Trash2Icon } from 'lucide-react'

import { Box, Checkbox, IconButton, Stack, Tooltip, Typography, useTheme } from '@mui/material'

import { Button } from '@repo/ui'
import { ConfirmSettingModal, Search } from 'components'

import { useAskStore } from 'ask'

const relative = (ts: number): string => {
  const mins = Math.floor((Date.now() - ts) / 60000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  if (days < 7) return `${days}d ago`
  return new Date(ts).toLocaleDateString()
}

/**
 * Both header rows are pinned to this so entering select mode CANNOT change the header's
 * height.
 *
 * A MUI `Checkbox` is 38px tall (a 20px glyph in 9px of padding), while the rest state's
 * header is a single line of `body4`. Swapping one for the other grew the header by ~18px,
 * which pushed the whole conversation list DOWN as the checkboxes slid in — so the
 * transition read as moving in two directions at once. The checkbox column already
 * animates its width, which is the horizontal half and is intended; this removes the
 * vertical half, leaving a clean single-axis reveal.
 *
 * 38 rather than a smaller number because it is the taller of the two states: shrinking to
 * fit the text line would mean squeezing the checkbox's own hit area, which is a worse
 * trade than a slightly taller resting header.
 */
const HEADER_ROW_HEIGHT_PX = 38

const plural = (n: number, noun: string) => `${n} ${noun}${n === 1 ? '' : 's'}`

/**
 * A plain underlined text action — the filter drawer's `Clear` treatment
 * (`FilterField.tsx`), not a `@repo/ui` Button. A `tertiary` Button's fill is
 * `white[100]` in light mode, which is the SAME white as this panel's chrome
 * surface (`sidebarSurface` — see the panel's own "chrome, not a card" note),
 * so it rendered as a near-invisible floating label. Plain text has no fill to
 * clash with.
 *
 * Always rendered and toggled with `visibility`, never unmounted — mirrors
 * `FilterField`'s own Clear — so a neighbour doesn't shift position as the
 * selection count crosses zero.
 */
const TextAction = ({
  children,
  onClick,
  visible = true,
  disabled = false,
  destructive = false,
}: {
  children: React.ReactNode
  onClick?: () => void
  visible?: boolean
  disabled?: boolean
  destructive?: boolean
}) => {
  const theme = useTheme()
  const fire = () => {
    if (!disabled) onClick?.()
  }
  return (
    <Typography
      component="span"
      role="button"
      tabIndex={disabled ? -1 : 0}
      onClick={fire}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          fire()
        }
      }}
      sx={{
        fontSize: 14,
        textDecoration: 'underline',
        cursor: disabled ? 'default' : 'pointer',
        visibility: visible ? 'visible' : 'hidden',
        color: disabled ? 'text.disabled' : 'text.secondary',
        outline: 'none',
        transition: 'color 120ms ease',
        flexShrink: 0,
        ...(!disabled && {
          '&:hover': { color: destructive ? theme.palette.error.main : theme.palette.text.primary },
          '&:focus-visible': { color: destructive ? theme.palette.error.main : theme.palette.text.primary },
        }),
      }}
    >
      {children}
    </Typography>
  )
}

export const AskHistoryView = () => {
  const theme = useTheme()
  const conversations = useAskStore(s => s.conversations)
  const activeId = useAskStore(s => s.activeId)
  const openConversation = useAskStore(s => s.openConversation)
  const deleteConversation = useAskStore(s => s.deleteConversation)
  const deleteConversations = useAskStore(s => s.deleteConversations)

  const [search, setSearch] = useState('')
  // Checkboxes are hidden until the user opts in — mirrors the shared `Table`'s
  // `showRowSelection` (`Table.tsx`): resting state is search + a "Select" affordance,
  // entering select mode reveals the checkboxes + bulk actions, leaving it hides both.
  const [selectMode, setSelectMode] = useState(false)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  /** `'selected'` / `'all'` are the bulk targets; a `{ id }` targets ONE row (see the
   *  `isSingleTarget` note below on why the single-row path is gated too). */
  const [confirmTarget, setConfirmTarget] = useState<'selected' | 'all' | { id: string } | null>(null)

  // Title AND message text — both are already in memory (max MAX_HISTORY=30
  // conversations, a handful of messages each), so filtering the transcript costs
  // nothing extra and finds a lot more than the title alone would: the title is just
  // the first user message, truncated, so a search for a word from turn 4 of a long
  // conversation would otherwise come up empty.
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return conversations
    return conversations.filter(
      c =>
        c.title.toLowerCase().includes(q) ||
        c.messages.some(m => m.text.toLowerCase().includes(q)),
    )
  }, [conversations, search])

  const allVisibleSelected = filtered.length > 0 && filtered.every(c => selectedIds.has(c.id))
  const someVisibleSelected = filtered.some(c => selectedIds.has(c.id))

  const toggleRow = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  // Scoped to the VISIBLE list only — see the module docblock. Toggling this never
  // touches a selection made before the current search narrowed the list.
  const toggleSelectAllVisible = () => {
    setSelectedIds(prev => {
      const next = new Set(prev)
      if (allVisibleSelected) filtered.forEach(c => next.delete(c.id))
      else filtered.forEach(c => next.add(c.id))
      return next
    })
  }

  // Unconditionally clears the selection on either transition — mirrors `Table`'s
  // `bulkActionsToggle` (`apiRef.current.setRowSelectionModel([])` before flipping),
  // so a stale selection can never sit behind a hidden checkbox column.
  const toggleSelectMode = () => {
    setSelectedIds(new Set())
    setSelectMode(prev => !prev)
  }

  const dropFromSelection = (ids: string[]) => {
    if (ids.length === 0) return
    setSelectedIds(prev => {
      const next = new Set(prev)
      ids.forEach(id => next.delete(id))
      return next
    })
  }

  const handleConfirmDelete = () => {
    if (confirmTarget === 'selected') {
      const ids = Array.from(selectedIds)
      deleteConversations(ids)
      dropFromSelection(ids)
    } else if (confirmTarget === 'all') {
      // "All" means all currently VISIBLE — see the module docblock.
      const ids = filtered.map(c => c.id)
      deleteConversations(ids)
      dropFromSelection(ids)
    } else if (confirmTarget !== null) {
      deleteConversation(confirmTarget.id)
      dropFromSelection([confirmTarget.id])
    }
    setConfirmTarget(null)
  }

  const confirmCount =
    confirmTarget === 'selected' ? selectedIds.size : confirmTarget === 'all' ? filtered.length : 1

  /**
   * The single-row delete is gated too, not just the bulk actions.
   *
   * This was originally left un-gated as "a quick, low-friction remove", reserving the
   * confirm for bulk where a mis-click costs more. That reasoning weighs the SIZE of the
   * mistake and ignores its REVERSIBILITY, which is the axis that actually matters here
   * (§43: a gate exists to name a consequence you cannot undo). There is no undo and no
   * server copy — history lives only in this browser's localStorage — so a stray click on
   * a trash icon that only appears on hover destroys a conversation permanently, and the
   * user may not even register which row was under the cursor.
   *
   * A one-row loss is smaller than a bulk loss. It is equally irreversible, and that is
   * what earns the gate.
   */
  const isSingleTarget = confirmTarget !== null && confirmTarget !== 'selected' && confirmTarget !== 'all'
  const confirmTitle = isSingleTarget
    ? 'Delete this conversation?'
    : `Delete ${plural(confirmCount, 'conversation')}?`
  const confirmLabel = isSingleTarget
    ? 'Delete conversation'
    : `Delete ${plural(confirmCount, 'conversation')}`

  if (conversations.length === 0) {
    return (
      <Stack alignItems="center" justifyContent="center" spacing={3} sx={{ flex: 1, px: 6, py: 10 }}>
        <Box sx={{ color: 'text.secondary', display: 'flex' }}>
          <HistoryIcon strokeWidth={1} size={28} />
        </Box>
        <Typography variant="body3" color="text.primary" textAlign="center" sx={{ fontWeight: 600 }}>
          No past questions
        </Typography>
        <Typography variant="body4" color="text.secondary" textAlign="center" sx={{ lineHeight: 1.5 }}>
          Questions you ask are kept here so you can come back to them.
        </Typography>
      </Stack>
    )
  }

  return (
    <Stack sx={{ flex: 1, minHeight: 0 }}>
      <Stack spacing={2} sx={{ px: 3, pt: 2, pb: 1 }}>
        <Search
          value={search}
          onChange={setSearch}
          onClear={() => setSearch('')}
          placeholder="Search conversations"
          fullWidth
          debounce={false}
        />

        {selectMode ? (
          // SELECT MODE — checkboxes + bulk actions. A whole-row swap against the rest
          // state below, not a collapse: the content is categorically different (a
          // select-all checkbox and per-selection actions vs. a plain count), so there is
          // nothing to animate between them. The per-row checkboxes below DO collapse
          // rather than unmount, because those are the ones that would otherwise reflow
          // ~30 rows at once on toggle (mirrors `Table.tsx`'s `__select__` column).
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={2}
            sx={{ minHeight: HEADER_ROW_HEIGHT_PX }}
          >
            {/* `spacing={3}` (12px, the button-group gap of working-conventions §29), not
                the 4px this used to sit at. The checkbox and the count read as one unit;
                `Clear` is a separate ACTION and was close enough to the word "selected" to
                look like part of the same phrase. */}
            <Stack direction="row" alignItems="center" spacing={3} sx={{ minWidth: 0 }}>
              <Checkbox
                size="small"
                checked={allVisibleSelected}
                indeterminate={!allVisibleSelected && someVisibleSelected}
                onChange={toggleSelectAllVisible}
                disabled={filtered.length === 0}
                aria-label="Select all conversations"
              />
              {/* No CountBadge here. It rendered the selected count as a pill immediately
                  beside a label that already says "8 selected" — the same number twice, a
                  glance apart. The badge earns its place in `FilterField`, where the label
                  is a field NAME carrying no count of its own; here the sentence already
                  carries it, so the pill was weight without information.

                  Note the label slot is doing double duty: "8 selected" when a selection
                  exists, "9 conversations" when it does not. Both read as complete phrases,
                  which a bare pill plus the word "selected" would not. */}
              <Typography variant="body4" color="text.secondary" noWrap>
                {selectedIds.size > 0
                  ? `${selectedIds.size} selected`
                  : plural(filtered.length, 'conversation')}
              </Typography>
              <TextAction visible={selectedIds.size > 0} onClick={() => setSelectedIds(new Set())}>
                Clear
              </TextAction>
            </Stack>

            {/*
              EXACTLY ONE delete action is offered at a time, and which one depends on
              whether a selection exists.

              They were both shown at once, side by side, reading "Delete selected"
              "Delete all" — two labels a glance apart, differing by one word, with
              enormously different blast radius: one destroys the three rows you ticked,
              the other destroys every conversation in view. That is a mis-click trap of
              the worst kind, because the cheap action and the expensive one look alike
              AND sit adjacent, so the cost of missing by 40px is everything you have.

              Once a selection exists, the user has told us what they mean; "all" is no
              longer the action they are reaching for, so it does not need to be in reach.
              With nothing selected there is no ambiguity to protect against, and "Delete
              all" is the only sensible bulk verb, so it takes the slot.

              Both are always MOUNTED and toggled with `visibility` (the `TextAction`
              contract), so the row's width never changes and `Cancel` never moves under
              the cursor as a selection crosses zero.
            */}
            <Stack direction="row" spacing={3} alignItems="center" sx={{ flexShrink: 0 }}>
              {/*
                ONE delete control whose label and target follow the selection, not two
                controls with the idle one hidden.

                The previous version rendered both and toggled `visibility`, reasoning that
                keeping both mounted stops `Cancel` sliding under the cursor as the count
                crosses zero. That is true and it is not worth what it costs: a
                `visibility: hidden` element still occupies its width, so the hidden label
                left a permanent dead gap between the visible action and `Cancel` — visible
                enough to read as a broken layout, which is worse than the shift it was
                avoiding.

                One element solves both at once. There is nothing to hide, so no gap; and
                because it is the same node either way, `Cancel` only moves by the
                difference between two label widths rather than by a whole hidden control.
              */}
              <TextAction
                destructive
                disabled={filtered.length === 0}
                onClick={() => setConfirmTarget(selectedIds.size > 0 ? 'selected' : 'all')}
              >
                {selectedIds.size > 0 ? 'Delete selected' : 'Delete all'}
              </TextAction>
              <TextAction onClick={toggleSelectMode}>Cancel</TextAction>
            </Stack>
          </Stack>
        ) : (
          // REST STATE — search box + a "Select" affordance, nothing else.
          <Stack
            direction="row"
            alignItems="center"
            spacing={2}
            sx={{ minHeight: HEADER_ROW_HEIGHT_PX }}
          >
            {/* `Select` sits FIRST, ahead of the count — the same leading slot the
                select-all checkbox occupies in select mode. Entering select mode then
                swaps one leading control for another in place, instead of the eye having
                to re-find the affordance at the opposite end of the row. */}
            <TextAction disabled={filtered.length === 0} onClick={toggleSelectMode}>
              Select
            </TextAction>
            <Typography variant="body4" color="text.secondary" noWrap>
              {plural(filtered.length, 'conversation')}
            </Typography>
          </Stack>
        )}
      </Stack>

      {filtered.length === 0 ? (
        // Search matches nothing — distinct from "no history at all" above. This one
        // needs a way back to the full list, since the emptiness is the search's doing,
        // not the account's.
        <Stack alignItems="center" justifyContent="center" spacing={3} sx={{ flex: 1, px: 6, py: 10 }}>
          <Box sx={{ color: 'text.secondary', display: 'flex' }}>
            <SearchXIcon strokeWidth={1} size={28} />
          </Box>
          <Typography variant="body3" color="text.primary" textAlign="center" sx={{ fontWeight: 600 }}>
            No conversations match "{search.trim()}"
          </Typography>
          {/* `secondary`, not `tertiary`. Tertiary's light-mode fill is `white[100]` — the
              same white this panel's chrome surface resolves to — so it was an invisible
              box with a label floating in it, the same defect the header actions just shed.
              Secondary carries a border, which gives it an edge against the surface. */}
          <Button variant="secondary" onClick={() => setSearch('')}>
            Clear search
          </Button>
        </Stack>
      ) : (
        <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', px: 2, py: 1 }}>
          {filtered.map(c => {
            const active = c.id === activeId
            const checked = selectedIds.has(c.id)
            return (
              <Stack
                key={c.id}
                direction="row"
                alignItems="center"
                onClick={() => openConversation(c.id)}
                sx={{
                  px: 2,
                  py: 3,
                  borderRadius: 2,
                  cursor: 'pointer',
                  bgcolor: active ? theme.palette.brand['600-6%'] : 'transparent',
                  /**
                   * A CONSTANT gap, not a hover transform.
                   *
                   * The problem being solved is real: rows sitting flush make a hover
                   * highlight read as a band welded to its neighbours rather than as its
                   * own row. The previous fix was `transform: scaleY(0.94)` on hover,
                   * chosen because a margin change would reflow every row below it.
                   *
                   * But a vertical-only scale squashes the row's CONTENT along with its
                   * box — the title and timestamp compress 6% on the y-axis while staying
                   * full width, so the type is distorted on one axis and the row visibly
                   * deforms under the cursor. Anisotropic scaling of text is a distortion
                   * whatever the intent, and it is what "the hover looks weird" was.
                   *
                   * A permanent `mb` gets the separation with none of that: rows are
                   * always their own objects, hover only changes colour, and because the
                   * gap never changes it cannot reflow anything. The reflow argument
                   * applied to a margin that appears ON HOVER, not to one that is simply
                   * always there.
                   */
                  mb: '2px',
                  transition: 'background-color 120ms ease',
                  '&:hover': { bgcolor: theme.palette.brand['600-6%'] },
                  '&:hover .ask-history-remove': { opacity: 1 },
                }}
              >
                {/* Collapses via width/opacity/pointerEvents rather than unmounting
                    (mirrors `Table.tsx`'s `__select__` column, :1001/:1391/:1404) — the
                    margin-right collapses WITH it so the title sits flush at the row's own
                    `px:2` at rest, instead of leaving a dead 8px indent behind an invisible
                    checkbox. That is why this box owns its own right margin instead of the
                    row Stack's `spacing`. */}
                <Box
                  sx={{
                    width: selectMode ? 36 : 0,
                    marginRight: selectMode ? 2 : 0,
                    overflow: 'hidden',
                    opacity: selectMode ? 1 : 0,
                    pointerEvents: selectMode ? 'auto' : 'none',
                    transition: 'width 160ms ease, margin-right 160ms ease, opacity 160ms ease',
                    flexShrink: 0,
                  }}
                >
                  <Checkbox
                    size="small"
                    checked={checked}
                    onChange={() => toggleRow(c.id)}
                    onClick={e => e.stopPropagation()}
                    tabIndex={selectMode ? 0 : -1}
                    aria-label={`Select "${c.title}"`}
                  />
                </Box>
                <Stack direction="row" spacing={2} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography variant="body3" color="text.primary" noWrap>
                      {c.title}
                    </Typography>
                    <Typography variant="body4" color="text.secondary" noWrap sx={{ display: 'block' }}>
                      {relative(c.updatedAt)}
                    </Typography>
                  </Box>
                  <Tooltip title="Delete conversation">
                    {/* Trash, not an X (working-conventions §38): once history is kept
                        until the user deletes it, removing a conversation is a real
                        deletion — the record ceases to exist, with no undo — so the glyph
                        should carry that weight. (An earlier version of this control used
                        X on the theory that nothing server-side was at stake; that theory
                        no longer holds once "kept until deleted" is the product's own
                        framing of what this list is.) This opens the SAME confirm as the
                        bulk actions: a one-row loss is smaller, but it is equally
                        irreversible, and reversibility is what earns a gate. */}
                    <IconButton
                      className="ask-history-remove"
                      size="small"
                      aria-label={`Delete "${c.title}"`}
                      onClick={e => {
                        e.stopPropagation()
                        setConfirmTarget({ id: c.id })
                      }}
                      sx={{
                        opacity: 0,
                        transition: 'opacity 120ms ease, color 120ms ease',
                        flexShrink: 0,
                        color: 'text.secondary',
                        '&:hover': { color: theme.palette.error.main },
                      }}
                    >
                      <Trash2Icon strokeWidth={1} size={16} />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </Stack>
            )
          })}
        </Box>
      )}

      <ConfirmSettingModal
        open={confirmTarget !== null}
        onClose={() => setConfirmTarget(null)}
        onConfirm={handleConfirmDelete}
        destructive
        title={confirmTitle}
        // Three sentences, no em-dash-joined clauses (working-conventions §15). The
        // second sentence is the one that matters: a brand user reading "delete" beside
        // a panel that answers questions ABOUT their data has every reason to fear this
        // touches the data itself, so the scope is stated rather than left to be inferred.
        consequence="Deleted conversations can't be recovered. This only removes your record of what you asked. It doesn't change anything about your company's data."
        confirmLabel={confirmLabel}
      />
    </Stack>
  )
}
